import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import { Mascot } from "@/components/Mascot";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setPost(null);
    setRelated([]);
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });

    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}`);
        setPost(data);
        if (data.meta_title) document.title = `${data.meta_title} — SEO Revue`;
        const md = document.querySelector('meta[name="description"]');
        if (data.meta_description) {
          if (md) md.setAttribute("content", data.meta_description);
          else {
            const m = document.createElement("meta");
            m.name = "description"; m.content = data.meta_description;
            document.head.appendChild(m);
          }
        }
        // Fetch related in parallel-ish (non-blocking)
        try {
          const r = await api.get(`/blog/${slug}/related`, { params: { limit: 3 } });
          setRelated(r.data.posts || []);
        } catch { /* ignore */ }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen text-[#FAF0DE] relative overflow-hidden">
      <div className="mesh-bg" />

      <header className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <Mascot pose="newspaper" className="w-10 h-10" />
          <Wordmark className="h-5" text="#FAF0DE" accent="#FF8C42" />
        </Link>
        <Link to="/blog" className="text-xs font-mono uppercase tracking-widest text-[#FAF0DE]/60 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Tous les articles
        </Link>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="text-[#FAF0DE]/40 font-mono text-sm">Chargement…</div>
        ) : notFound || !post ? (
          <div className="text-[#FAF0DE]/60 text-center py-16">
            <div className="font-display text-3xl font-bold mb-2">Article introuvable</div>
            <Link to="/blog" className="text-[#FF8C42] underline underline-offset-4">Retour au blog</Link>
          </div>
        ) : (
          <>
            <article data-testid="blog-post-article">
              <div className="mb-6 text-xs font-mono uppercase tracking-widest text-[#FAF0DE]/40">
                {(post.published_at || "").slice(0, 10)}
                {post.tags?.length > 0 && (
                  <span> · {post.tags.map(t => `#${t}`).join(" · ")}</span>
                )}
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-black tracking-[-0.02em] leading-[1.02] mb-8">
                {post.title}
              </h1>

              {post.cover_image && (
                <div className="mb-10 rounded-3xl overflow-hidden border border-[#FAF0DE]/10">
                  <img src={post.cover_image} alt="" className="w-full h-auto" />
                </div>
              )}

              <p className="text-xl leading-relaxed text-[#FAF0DE]/85 mb-10 font-editorial">
                {post.excerpt}
              </p>

              <div
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:font-display prose-headings:text-[#FAF0DE] prose-headings:tracking-tight
                  prose-h2:mt-12 prose-h2:mb-4 prose-h3:mt-8
                  prose-p:text-[#FAF0DE]/80 prose-p:leading-relaxed
                  prose-a:text-[#FF8C42] prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-white
                  prose-blockquote:border-l-[#FF8C42] prose-blockquote:text-[#FAF0DE]/70
                  prose-img:rounded-2xl prose-img:border prose-img:border-[#FAF0DE]/10
                  prose-code:text-[#FFD600] prose-code:bg-[#FAF0DE]/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
                  prose-li:text-[#FAF0DE]/80
                "
                data-testid="blog-post-content"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content || ""}</ReactMarkdown>
              </div>
            </article>

            {related.length > 0 && (
              <section className="mt-20" data-testid="blog-related">
                <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-4">Continuer la lecture</div>
                <h2 className="font-display text-3xl font-black tracking-tight mb-6">Articles liés</h2>
                <div className="grid md:grid-cols-3 gap-5">
                  {related.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/blog/${p.slug}`}
                      data-testid={`related-post-${p.slug}`}
                      className="group rounded-2xl border border-[#FAF0DE]/10 bg-[#132038]/60 backdrop-blur p-5 hover:border-[#FF8C42]/60 transition-colors"
                    >
                      {p.cover_image && (
                        <div className="mb-4 rounded-lg overflow-hidden aspect-[16/10]">
                          <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      )}
                      {p.tags?.length > 0 && (
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF8C42]/80 mb-2">
                          #{p.tags[0]}
                        </div>
                      )}
                      <h3 className="font-display text-lg font-bold leading-snug mb-2 group-hover:text-[#FF8C42] transition-colors">
                        {p.title}
                      </h3>
                      <p className="text-sm text-[#FAF0DE]/60 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-16 rounded-3xl bg-gradient-to-br from-[#FF8C42] to-[#FFD600] p-8 md:p-10 text-[#0A1225]">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <Mascot pose="waving" className="w-24 h-24 shrink-0" />
                <div className="flex-1">
                  <h3 className="font-display text-2xl md:text-3xl font-black mb-2">Vous voulez tout, plus tôt ?</h3>
                  <p className="opacity-80">Accès complet à la veille SEO temps réel — gratuit pendant la bêta.</p>
                </div>
                <Link to="/dashboard"
                  className="bg-[#0A1225] text-[#FAF0DE] px-6 py-3 rounded-full font-mono text-sm uppercase tracking-widest hover:bg-[#FAF0DE] hover:text-[#0A1225] transition-colors whitespace-nowrap flex items-center gap-2">
                  Accéder gratuitement <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
